import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Search, Upload, X, Plus } from "lucide-react";
import ImagePositioner from "./ImagePositioner";

const MAX_GALLERY = 5;

// Normalize stored gallery items — old deals store plain URL strings,
// newer ones store { url, credit } or { url, credit, position } objects.
function normalize(item) {
  if (typeof item === "string") return { url: item, credit: null, position: null };
  return { url: item.url, credit: item.credit ?? null, position: item.position ?? null };
}

export default function GalleryPicker({ gallery, onChange }) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addPhoto = (url, credit = null) => {
    if (gallery.length >= MAX_GALLERY) return;
    const updated = [...gallery, { url, credit, position: null }];
    onChange(updated);
    setAdding(false);
    setResults([]);
    setQuery("");
  };

  const updatePosition = (idx, position) => {
    const updated = gallery.map((item, i) => {
      if (i !== idx) return item;
      const n = normalize(item);
      return { ...n, position };
    });
    onChange(updated);
  };

  const updateCredit = (idx, credit) => {
    const updated = gallery.map((item, i) => {
      if (i !== idx) return item;
      const n = normalize(item);
      return { ...n, credit: credit || null };
    });
    onChange(updated);
  };

  const removePhoto = (idx) => {
    onChange(gallery.filter((_, i) => i !== idx));
  };

  const searchUnsplash = async () => {
    if (!query.trim()) { toast.error("Enter a search term"); return; }
    setSearching(true);
    try {
      const { data } = await api.post("/admin/unsplash-search", { query: query.trim() });
      setResults(data.results || []);
      if (!data.results?.length) toast.error("No photos found — try a different query");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      addPhoto(data.image_url, null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="gallery-picker">
      {/* Gallery items with focal point controls */}
      {gallery.length > 0 && (
        <div className="space-y-4" data-testid="gallery-thumbnails">
          {gallery.map((item, idx) => {
            const { url, credit, position } = normalize(item);
            return (
              <div key={idx} className="border border-[#2A2A2A] bg-[#0E0E0E] p-3" data-testid={`gallery-thumb-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono tracking-[0.14em] uppercase text-[#555]">
                    Photo {idx + 2}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="flex items-center gap-1 text-[10px] font-mono tracking-[0.14em] uppercase text-[#555] hover:text-[#CC1A1A] transition-colors"
                    aria-label={`Remove photo ${idx + 2}`}
                    data-testid={`gallery-remove-${idx}`}
                  >
                    <X className="w-3 h-3" /> REMOVE
                  </button>
                </div>
                <ImagePositioner
                  url={url}
                  position={position}
                  onChange={(pos) => updatePosition(idx, pos)}
                />
                <div className="mt-2">
                  <label className="block text-[10px] font-mono tracking-[0.2em] uppercase text-[#555] mb-1">
                    Photo credit (optional)
                  </label>
                  <input
                    type="text"
                    value={credit || ""}
                    onChange={(e) => updateCredit(idx, e.target.value)}
                    placeholder="e.g. Courtesy of Trip.com"
                    className="w-full bg-[#111] border border-[#2A2A2A] text-[#E5E0D8] font-mono text-xs px-3 py-2 outline-none focus:border-[#CC1A1A] transition-colors"
                    data-testid={`gallery-credit-${idx}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add photo button */}
      {gallery.length < MAX_GALLERY && !adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-2 text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white border border-[#2A2A2A] hover:border-[#555] px-4 py-3 transition-colors w-full justify-center"
          data-testid="gallery-add-btn"
        >
          <Plus className="w-3 h-3" />
          + ADD ANOTHER PHOTO ({gallery.length}/{MAX_GALLERY})
        </button>
      )}

      {/* Max reached */}
      {gallery.length >= MAX_GALLERY && (
        <p className="text-[11px] font-mono tracking-[0.14em] uppercase text-[#555]">
          MAXIMUM 5 ADDITIONAL PHOTOS REACHED
        </p>
      )}

      {/* Inline picker */}
      {adding && (
        <div className="border border-[#2A2A2A] bg-[#0E0E0E] p-4 space-y-4" data-testid="gallery-inline-picker">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83]">
              Photo {gallery.length + 2} of 6
            </p>
            <button
              type="button"
              onClick={() => { setAdding(false); setResults([]); setQuery(""); }}
              className="text-[11px] font-mono tracking-[0.18em] uppercase text-[#555] hover:text-white transition-colors"
            >
              CANCEL
            </button>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchUnsplash(); } }}
              placeholder="Search Unsplash…"
              className="ed-input flex-1"
              data-testid="gallery-search-input"
              inputMode="search"
            />
            <button
              type="button"
              onClick={searchUnsplash}
              disabled={searching}
              className="btn-rd !py-0 !px-4 h-[44px] !text-[0.7rem] whitespace-nowrap"
              data-testid="gallery-search-btn"
            >
              <Search className="w-4 h-4" /> {searching ? "…" : "SEARCH"}
            </button>
            <label className="btn-rd-yellow h-[44px] !py-0 cursor-pointer flex items-center gap-2 px-4 whitespace-nowrap" data-testid="gallery-upload-label">
              <Upload className="w-4 h-4" /> {uploading ? "…" : "UPLOAD"}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={uploadFile}
                data-testid="gallery-upload-input"
              />
            </label>
          </div>

          {results.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="gallery-search-results">
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => addPhoto(r.regular, r.photographer ? `Photo by ${r.photographer} on Unsplash` : null)}
                  className="relative aspect-square overflow-hidden border-2 border-transparent hover:border-[#FFDD57] transition-colors"
                  data-testid={`gallery-result-${r.id}`}
                >
                  <img src={r.thumb} alt={r.alt} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
